import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Subscription, catchError, forkJoin, map, of, timer } from 'rxjs';
import { AdminNotification } from '../models/admin-notification.models';
import { ModerationService } from './moderation.service';
import { ReportsService } from './reports.service';
import { UiPreferencesService } from './ui-preferences.service';

@Injectable({ providedIn: 'root' })
export class AdminNotificationsService {
  private readonly moderation = inject(ModerationService);
  private readonly reports = inject(ReportsService);
  private readonly uiPreferences = inject(UiPreferencesService);
  private readonly readStorageKey = 'afrotchop.admin.notifications.read.v1';
  private readonly pollIntervalMs = 60_000;

  private readonly notificationsSubject = new BehaviorSubject<AdminNotification[]>([]);
  readonly notifications$ = this.notificationsSubject.asObservable();

  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  readonly unreadCount$ = this.unreadCountSubject.asObservable();

  private pollingSubscription: Subscription | null = null;
  private readonly readIds = new Set<string>();
  private readonly browserNotifiedIds = new Set<string>();
  private canAccessReports = false;

  constructor() {
    this.readIds = this.loadReadIds();
  }

  startPolling(canAccessReports: boolean): void {
    this.canAccessReports = canAccessReports;

    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }

    this.pollingSubscription = timer(0, this.pollIntervalMs).subscribe(() => {
      this.refresh();
    });
  }

  stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  refresh(): void {
    const reportsRequest = this.canAccessReports
      ? this.reports.getReports('OPEN', 0, 6).pipe(
          map((response) => response.items ?? []),
          catchError(() => of([]))
        )
      : of([]);

    forkJoin({
      recipes: this.moderation.getRecipes('REVIEW', 0, 6).pipe(
        map((response) => response.items ?? []),
        catchError(() => of([]))
      ),
      inspirations: this.moderation.getInspirations('REVIEW', 0, 6).pipe(
        map((response) => response.items ?? []),
        catchError(() => of([]))
      ),
      comments: this.moderation.getReviewMessages(0, 6).pipe(
        map((response) => response.items ?? []),
        catchError(() => of([]))
      ),
      reports: reportsRequest,
    }).subscribe(({ recipes, inspirations, comments, reports }) => {
      const mappedRecipes = recipes.map<AdminNotification>((recipe) => ({
        id: `recipe:${recipe.id}`,
        kind: 'recipe_review',
        title: 'Nouvelle recette à modérer',
        description: recipe.name,
        createdAt: recipe.createdAt,
        route: '/dashboard/moderation',
        isRead: this.readIds.has(`recipe:${recipe.id}`),
      }));

      const mappedInspirations = inspirations.map<AdminNotification>((inspiration) => ({
        id: `inspiration:${inspiration.id}`,
        kind: 'inspiration_review',
        title: 'Nouvelle inspiration à modérer',
        description: inspiration.title?.trim() || `Inspiration #${inspiration.id}`,
        createdAt: inspiration.createdAt,
        route: '/dashboard/moderation',
        isRead: this.readIds.has(`inspiration:${inspiration.id}`),
      }));

      const mappedComments = comments.map<AdminNotification>((review) => ({
        id: `comment:${review.id}`,
        kind: 'comment_review',
        title: 'Nouveau commentaire à modérer',
        description: review.authorDisplayName
          ? `${review.authorDisplayName}: ${review.message ?? ''}`.trim()
          : review.message ?? `Commentaire #${review.id}`,
        createdAt: review.createdAt,
        route: '/dashboard/moderation',
        isRead: this.readIds.has(`comment:${review.id}`),
      }));

      const mappedReports = reports.map<AdminNotification>((report) => ({
        id: `report:${report.id}`,
        kind: 'report_open',
        title: 'Nouveau signalement ouvert',
        description: `${report.targetType} #${report.targetId} - ${report.reason}`,
        createdAt: report.createdAt,
        route: '/dashboard/reports',
        isRead: this.readIds.has(`report:${report.id}`),
      }));

      const merged = [...mappedRecipes, ...mappedInspirations, ...mappedComments, ...mappedReports].sort((a, b) => {
        return this.toTimestamp(b.createdAt) - this.toTimestamp(a.createdAt);
      });

      this.notificationsSubject.next(merged);
      this.unreadCountSubject.next(merged.filter((item) => !item.isRead).length);
      this.maybeSendBrowserNotifications(merged);
    });
  }

  markAllRead(): void {
    const notifications = this.notificationsSubject.value;
    if (notifications.length === 0) {
      return;
    }

    notifications.forEach((notification) => this.readIds.add(notification.id));
    this.persistReadIds();
    this.notificationsSubject.next(
      notifications.map((notification) => ({
        ...notification,
        isRead: true,
      }))
    );
    this.unreadCountSubject.next(0);
  }

  markAsRead(notificationId: string): void {
    this.readIds.add(notificationId);
    this.persistReadIds();
    const updated = this.notificationsSubject.value.map((notification) => {
      if (notification.id !== notificationId) {
        return notification;
      }
      return {
        ...notification,
        isRead: true,
      };
    });
    this.notificationsSubject.next(updated);
    this.unreadCountSubject.next(updated.filter((item) => !item.isRead).length);
  }

  get permission(): NotificationPermission | 'unsupported' {
    if (typeof Notification === 'undefined') {
      return 'unsupported';
    }
    return Notification.permission;
  }

  async requestBrowserPermission(): Promise<NotificationPermission | 'unsupported'> {
    if (typeof Notification === 'undefined') {
      return 'unsupported';
    }
    return Notification.requestPermission();
  }

  private maybeSendBrowserNotifications(notifications: AdminNotification[]): void {
    if (!this.uiPreferences.notificationsEnabled || typeof Notification === 'undefined') {
      return;
    }

    if (Notification.permission !== 'granted') {
      return;
    }

    const fresh = notifications
      .filter((item) => !item.isRead)
      .filter((item) => !this.browserNotifiedIds.has(item.id))
      .slice(0, 3);

    fresh.forEach((item) => {
      this.browserNotifiedIds.add(item.id);
      const notification = new Notification(item.title, {
        body: item.description,
      });
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    });
  }

  private loadReadIds(): Set<string> {
    if (typeof window === 'undefined') {
      return new Set<string>();
    }

    const raw = window.localStorage.getItem(this.readStorageKey);
    if (!raw) {
      return new Set<string>();
    }

    try {
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) {
        return new Set<string>();
      }
      return new Set<string>(parsed.filter((value): value is string => typeof value === 'string'));
    } catch {
      return new Set<string>();
    }
  }

  private persistReadIds(): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(this.readStorageKey, JSON.stringify(Array.from(this.readIds)));
  }

  private toTimestamp(value: string | number | undefined): number {
    if (value == null) {
      return 0;
    }

    if (typeof value === 'number') {
      const timestamp = value > 1_000_000_000_000 ? value : value * 1000;
      return Number.isFinite(timestamp) ? timestamp : 0;
    }

    const normalized = value.trim();
    if (!normalized) {
      return 0;
    }

    if (/^\d+$/.test(normalized)) {
      const numeric = Number.parseInt(normalized, 10);
      if (Number.isFinite(numeric)) {
        return numeric > 1_000_000_000_000 ? numeric : numeric * 1000;
      }
      return 0;
    }

    const parsed = Date.parse(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }
}
